import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo, Wordmark } from "../components/Logo";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const uaShort = (ua = "") => {
  const mob = /Mobile|Android|iPhone|iPad/i.test(ua) ? " · Mobile" : "";
  const b = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox"
    : /Safari/i.test(ua) ? "Safari" : /bot|crawl|spider/i.test(ua) ? "Bot" : "Other";
  return b + mob;
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [visits, setVisits] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads`);
      setLeads(data.leads);
    } catch (e) {
      if (e.response?.status === 401) { logout(); navigate("/admin/login"); }
      else setErr("Could not load leads.");
    }
    try {
      const { data } = await axios.get(`${API}/visits`);
      setVisits(data);
    } catch { /* non-blocking */ }
  }, [logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    try {
      const { data } = await axios.get(`${API}/leads/${id}`);
      setDetail(data);
    } catch { setErr("Could not load that report."); }
  };

  const totalCrit = leads ? leads.reduce((s, l) => s + (l.critical || 0), 0) : 0;

  return (
    <div className="wrap" style={{ paddingTop: 8, paddingBottom: 60 }}>
      <div className="admin-top">
        <a className="brand" href="/" style={{ display: "flex", alignItems: "center", gap: 10 }}
          onClick={(e) => { e.preventDefault(); navigate("/"); }}>
          <Logo /> <Wordmark />
        </a>
        <span className="spacer" />
        <span className="kv" data-testid="admin-user">{user?.email}</span>
        <button className="btn ghost" data-testid="logout-btn" onClick={() => { logout(); navigate("/admin/login"); }}>Log out</button>
      </div>

      <div className="eyebrow">Private · team only</div>
      <h1 style={{ fontSize: "clamp(24px,3.4vw,32px)", color: "var(--head)", margin: "10px 0 4px", letterSpacing: "-1px" }}>VeriScan Leads</h1>
      <p className="lede" style={{ marginBottom: 18 }}>Every code-scan submission, most recent first. Click a row to open the full report.</p>

      {leads && (
        <div className="stats" style={{ marginBottom: 18 }}>
          <div className="stat"><div className="n">{leads.length}</div><div className="l">Leads</div></div>
          <div className="stat"><div className="n">{leads.reduce((s, l) => s + (l.findings || 0), 0)}</div><div className="l">Findings</div></div>
          <div className="stat"><div className="n" style={{ color: "var(--red)" }}>{totalCrit}</div><div className="l">Critical</div></div>
          <div className="stat"><div className="n" style={{ color: "var(--green)" }}>{leads.filter((l) => l.delivery === "sent").length}</div><div className="l">Emailed</div></div>
        </div>
      )}
      <div className="err" data-testid="admin-error">{err}</div>

      <div className="table-wrap" data-testid="leads-table">
        {leads === null ? <div className="empty">Loading…</div>
          : leads.length === 0 ? <div className="empty">No leads yet. Submissions from /scan will appear here.</div>
          : (
            <table className="leads">
              <thead><tr>
                <th>Name</th><th>Company</th><th>Email</th><th>Role</th>
                <th>Source</th><th>Files</th><th>Findings</th><th>Critical</th><th>Email</th><th>Date</th>
              </tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className={detail?.id === l.id ? "active" : ""} onClick={() => open(l.id)}>
                    <td style={{ color: "var(--head)", fontWeight: 600 }}>{l.name || "—"}</td>
                    <td>{l.company || "—"}</td>
                    <td>{l.email}</td>
                    <td>{l.role || "—"}</td>
                    <td className="kv">{l.source}</td>
                    <td>{l.files_scanned}</td>
                    <td>{l.findings}</td>
                    <td className={l.critical ? "crit" : ""}>{l.critical}</td>
                    <td><span className={`pill ${l.delivery}`}>{l.delivery}</span></td>
                    <td className="kv">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <div className="eyebrow" style={{ marginTop: 40 }}>Site analytics</div>
      <h1 style={{ fontSize: "clamp(22px,3vw,28px)", color: "var(--head)", margin: "10px 0 4px", letterSpacing: "-1px" }}>Visitors</h1>
      <p className="lede" style={{ marginBottom: 18 }}>Human page views across the public site (admin pages & bots excluded), with approximate location and network for each IP.</p>
      {visits && (
        <>
          <div className="stats" style={{ marginBottom: 18 }}>
            <div className="stat"><div className="n" data-testid="visits-total">{visits.total}</div><div className="l">Visits</div></div>
            <div className="stat"><div className="n" style={{ color: "var(--green)" }} data-testid="visits-sessions">{visits.unique_sessions}</div><div className="l">Unique sessions</div></div>
            <div className="stat"><div className="n" data-testid="visits-unique">{visits.unique}</div><div className="l">Unique IPs</div></div>
            <div className="stat"><div className="n" style={{ color: "var(--dim)" }} data-testid="visits-bots">{visits.bots}</div><div className="l">Bots filtered</div></div>
          </div>
          {visits.daily && (() => {
            const max = Math.max(1, ...visits.daily.map((d) => d.count));
            return (
              <div className="chart" data-testid="visits-chart">
                {visits.daily.map((d) => (
                  <div key={d.date} className="barwrap" title={`${d.date}: ${d.count} visit${d.count !== 1 ? "s" : ""}`}>
                    <div className="bar" style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }} />
                    <span className="blabel">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </>
      )}
      <div className="table-wrap" data-testid="visits-table" style={{ marginTop: 16 }}>
        {!visits ? <div className="empty">Loading…</div>
          : visits.recent.length === 0 ? <div className="empty">No human visits recorded yet.</div>
          : (
            <table className="leads">
              <thead><tr><th>IP address</th><th>Location</th><th>Network / Org</th><th>Page</th><th>Browser</th><th>When</th></tr></thead>
              <tbody>
                {visits.recent.map((v) => {
                  const g = v.geo || {};
                  const loc = [g.city, g.country].filter(Boolean).join(", ");
                  return (
                    <tr key={v.id} style={{ cursor: "default" }}>
                      <td style={{ color: "var(--head)", fontWeight: 600 }} className="kv">{v.ip || "—"}</td>
                      <td>{loc || "—"}</td>
                      <td className="kv" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.org || g.isp || "—"}</td>
                      <td className="kv">{v.path}</td>
                      <td>{uaShort(v.ua)}</td>
                      <td className="kv">{fmtDate(v.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>

      <AnimatePresence>
        {detail && (
          <>
            <motion.div className="backdrop" data-testid="drawer-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setDetail(null)} />
            <motion.div className="drawer" data-testid="lead-drawer"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}>
              <div className="dhead">
                <div style={{ minWidth: 0, overflow: "hidden" }}>
                  <div style={{ color: "var(--head)", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{detail.name} · {detail.company || "no company"}</div>
                  <div className="kv" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{detail.email} · {detail.findings} findings · {detail.critical} critical</div>
                </div>
                <button className="btn ghost" data-testid="drawer-close" style={{ flexShrink: 0, marginLeft: "auto" }} onClick={() => setDetail(null)}>Close ✕</button>
              </div>
              <iframe title="Full report" data-testid="report-frame" srcDoc={detail.report_html} sandbox="" />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
